import { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';
import PropTypes from 'prop-types';

// Create Notification Context
const NotificationContext = createContext();

/**
 * Notification Provider Component
 * Wrap your app with this provider to enable notifications throughout the app
 */
export function NotificationProvider({ children }) {
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info', // 'success', 'error', 'warning', 'info'
    duration: 5000,
  });

  const showNotification = useCallback((message, severity = 'info', duration = null) => {
    // Auto-determine duration based on message length and severity
    let autoDuration = duration;
    if (!autoDuration) {
      if (severity === 'error') {
        // Longer duration for errors, especially if message is long
        const messageLength = typeof message === 'string' ? message.length : 100;
        autoDuration = Math.min(Math.max(7000, messageLength * 50), 15000); // 7-15 seconds for errors
      } else {
        autoDuration = 5000; // 5 seconds for other types
      }
    }

    setNotification({
      open: true,
      message,
      severity,
      duration: autoDuration,
    });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    hideNotification();
  };

  return (
    <NotificationContext.Provider value={{ showNotification, hideNotification }}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleClose}
          severity={notification.severity}
          sx={{
            width: '100%',
            maxWidth: '600px',
            border: '1px solid',
            borderColor: notification.severity === 'error' ? '#ef4444' :
                         notification.severity === 'success' ? '#10b981' :
                         notification.severity === 'warning' ? '#f59e0b' :
                         '#3b82f6',
            whiteSpace: 'pre-wrap',
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to use notifications in any component
 *
 * @example
 * const { showNotification } = useNotification();
 *
 * // Show success notification
 * showNotification('Operation successful!', 'success');
 *
 * // Show error notification
 * showNotification('Something went wrong', 'error');
 *
 * // Show warning notification
 * showNotification('Please be careful', 'warning');
 *
 * // Show info notification
 * showNotification('Here is some info', 'info');
 */
export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}
